import { Component } from '@theme/component';

/**
 * Reads and writes the favorited product ids to localStorage.
 */
export class ProductFavorites {
  static #STORAGE_KEY = 'favoriteProducts';

  /** @returns {string[]} */
  static getIds() {
    try {
      return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  /** @param {string} productId */
  static has(productId) {
    return this.getIds().includes(productId);
  }

  /**
   * Toggles a product's favorited state.
   * @param {string} productId
   * @returns {boolean} The new favorited state.
   */
  static toggle(productId) {
    const ids = this.getIds();
    const index = ids.indexOf(productId);
    const active = index === -1;

    if (active) {
      ids.push(productId);
    } else {
      ids.splice(index, 1);
    }

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(ids));
    return active;
  }
}

/**
 * A heart toggle button on a product card that saves the product to a
 * localStorage-backed favorites list. No account or app required.
 *
 * @typedef {object} ProductFavoriteButtonRefs
 * @property {HTMLButtonElement} button
 * @extends {Component<ProductFavoriteButtonRefs>}
 */
export class ProductFavoriteButton extends Component {
  requiredRefs = ['button'];

  connectedCallback() {
    super.connectedCallback();
    this.#sync(ProductFavorites.has(this.productId));
  }

  get productId() {
    return this.getAttribute('data-product-id') ?? '';
  }

  /** @param {Event} event */
  toggle(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.productId) return;

    this.#sync(ProductFavorites.toggle(this.productId));
  }

  /** @param {boolean} active */
  #sync(active) {
    this.toggleAttribute('data-active', active);

    const { button } = this.refs;
    button.setAttribute('aria-pressed', String(active));

    const label = active ? Theme.translations?.remove_from_favorites : Theme.translations?.add_to_favorites;
    if (label) button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('product-favorite-component')) {
  customElements.define('product-favorite-component', ProductFavoriteButton);
}
